import { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BookOpenIcon, GlobeAltIcon, BuildingOfficeIcon, CpuChipIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	DataTable,
	Badge,
	FilterDropdown,
	TextStack,
} from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getKbArticles } from '../../api/knowledgeBase';

const CATEGORIES = [
	{ value: 'all', label: 'All Categories' },
	{ value: 'troubleshooting', label: 'Troubleshooting' },
	{ value: 'maintenance', label: 'Maintenance' },
	{ value: 'howto', label: 'How-To' },
	{ value: 'faq', label: 'FAQ' },
	{ value: 'reference', label: 'Reference' },
	{ value: 'safety', label: 'Safety' },
];

const STATUSES = [
	{ value: 'all', label: 'All Statuses' },
	{ value: 'draft', label: 'Draft' },
	{ value: 'published', label: 'Published' },
	{ value: 'archived', label: 'Archived' },
];

const SCOPE_TYPES = [
	{ value: 'all', label: 'All Scopes' },
	{ value: 'generic', label: 'Generic' },
	{ value: 'manufacturer', label: 'Per Manufacturer' },
	{ value: 'model', label: 'Per Model' },
];

const VISIBILITIES = [
	{ value: 'all', label: 'All Visibility' },
	{ value: 'internal', label: 'Internal Only' },
	{ value: 'public', label: 'Public' },
];

const ScopeIcon = ({ scopeType }) => {
	switch (scopeType) {
		case 'manufacturer':
			return <BuildingOfficeIcon className="w-4 h-4 text-blue-500" />;
		case 'model':
			return <CpuChipIcon className="w-4 h-4 text-purple-500" />;
		default:
			return <GlobeAltIcon className="w-4 h-4 text-gray-400" />;
	}
};

const KnowledgeBase = () => {
	const { setOpen: openEditKbArticle } = useModal('editKbArticle');

	// Filters
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [categoryFilter, setCategoryFilter] = useState('all');
	const [scopeFilter, setScopeFilter] = useState('all');
	const [visibilityFilter, setVisibilityFilter] = useState('all');

	// Data
	const {
		value: articlesData,
		execute: refreshArticles,
		status: loadingStatus,
	} = useAsync(getKbArticles);

	useEffect(() => {
		refreshArticles();
	}, []);

	// Process articles (search and filters)
	const processedArticles = useMemo(() => {
		let result = articlesData?.rows || [];

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(article) =>
					article.title?.toLowerCase().includes(q) ||
					article.summary?.toLowerCase().includes(q) ||
					article.articleCode?.toLowerCase().includes(q) ||
					article.tags?.some((tag) => tag.toLowerCase().includes(q)),
			);
		}

		// Status filter
		if (statusFilter && statusFilter !== 'all') {
			result = result.filter((article) => article.status === statusFilter);
		}

		// Category filter
		if (categoryFilter && categoryFilter !== 'all') {
			result = result.filter((article) => article.category === categoryFilter);
		}

		// Scope filter
		if (scopeFilter && scopeFilter !== 'all') {
			result = result.filter((article) => article.scopeType === scopeFilter);
		}

		// Visibility filter
		if (visibilityFilter && visibilityFilter !== 'all') {
			result = result.filter((article) => article.visibility === visibilityFilter);
		}

		return result;
	}, [articlesData, search, statusFilter, categoryFilter, scopeFilter, visibilityFilter]);

	const handleRowClick = (article) => {
		openEditKbArticle({
			article,
			onSuccess: () => refreshArticles(),
		});
	};

	const isLoading = loadingStatus === 'pending';

	const columns = [
		{
			key: 'status',
			header: 'Status',
			width: '110px',
			render: (article) => (
				<Badge preset="status" value={article.status || 'draft'} />
			),
		},
		{
			key: 'articleCode',
			header: 'Code',
			width: '100px',
			render: (article) => (
				<span className="font-mono text-sm text-gray-600">{article.articleCode}</span>
			),
		},
		{
			key: 'title',
			header: 'Title',
			render: (article) => (
				<TextStack
					primary={article.title}
					secondary={article.summary || 'No summary'}
				/>
			),
		},
		{
			key: 'category',
			header: 'Category',
			width: '130px',
			render: (article) => {
				if (!article.category) {
					return <span className="text-gray-400 text-sm">—</span>;
				}
				return <Badge preset="category" value={article.category} />;
			},
		},
		{
			key: 'scope',
			header: 'Scope',
			width: '150px',
			render: (article) => {
				const scopeLabels = {
					generic: 'Generic',
					manufacturer: 'Manufacturer',
					model: 'Model',
				};

				let scopeDetail = '';
				if (article.scopeType === 'manufacturer' && article.manufacturers?.length) {
					scopeDetail = article.manufacturers[0].name;
				} else if (article.scopeType === 'model' && article.models?.length) {
					scopeDetail = article.models[0].name;
				}

				return (
					<div className="flex items-center gap-2">
						<ScopeIcon scopeType={article.scopeType} />
						<div className="flex flex-col">
							<span className="text-sm">{scopeLabels[article.scopeType] || 'Generic'}</span>
							{scopeDetail && (
								<span className="text-xs text-gray-500 truncate max-w-[100px]">{scopeDetail}</span>
							)}
						</div>
					</div>
				);
			},
		},
		{
			key: 'visibility',
			header: 'Visibility',
			width: '100px',
			render: (article) => (
				<Badge
					variant={article.visibility === 'public' ? 'success' : 'muted'}
					style="outline"
				>
					{article.visibility === 'public' ? 'Public' : 'Internal'}
				</Badge>
			),
		},
		{
			key: 'updatedAt',
			header: 'Updated',
			width: '120px',
			render: (article) => (
				<span className="text-sm text-gray-500">
					{moment(article.updatedAt || article.createdAt).format('MMM D, YYYY')}
				</span>
			),
		},
	];

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading
				actions={[
					{
						name: 'Create Article',
						onClick: () =>
							openEditKbArticle({
								article: null,
								onSuccess: () => refreshArticles(),
							}),
					},
				]}
				divider={false}
			>
				<div className="flex items-center gap-3">
					<BookOpenIcon className="w-7 h-7 text-blue-600" />
					Knowledge Base
				</div>
			</PageHeading>

			{/* Stats Cards */}
			<div className="grid grid-cols-4 gap-4 mb-6">
				<div className="bg-white border border-gray-200 rounded-lg p-4">
					<div className="text-2xl font-semibold text-gray-900">
						{articlesData?.rows?.length || 0}
					</div>
					<div className="text-sm text-gray-500">Total Articles</div>
				</div>
				<div className="bg-white border border-gray-200 rounded-lg p-4">
					<div className="text-2xl font-semibold text-green-600">
						{articlesData?.rows?.filter((a) => a.status === 'published').length || 0}
					</div>
					<div className="text-sm text-gray-500">Published</div>
				</div>
				<div className="bg-white border border-gray-200 rounded-lg p-4">
					<div className="text-2xl font-semibold text-yellow-600">
						{articlesData?.rows?.filter((a) => a.status === 'draft').length || 0}
					</div>
					<div className="text-sm text-gray-500">Drafts</div>
				</div>
				<div className="bg-white border border-gray-200 rounded-lg p-4">
					<div className="text-2xl font-semibold text-blue-600">
						{articlesData?.rows?.filter((a) => a.visibility === 'public').length || 0}
					</div>
					<div className="text-sm text-gray-500">Public</div>
				</div>
			</div>

			{/* Search and Filters */}
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				<div className="flex items-center gap-4 p-4 flex-wrap">
					<div className="flex-1 relative min-w-[200px]">
						<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search articles..."
							className="w-full pl-12 pr-4 py-3 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
						/>
					</div>
					<FilterDropdown
						label="Status"
						placeholder="All Statuses"
						value={statusFilter}
						options={STATUSES}
						onChange={setStatusFilter}
					/>
					<FilterDropdown
						label="Category"
						placeholder="All Categories"
						value={categoryFilter}
						options={CATEGORIES}
						onChange={setCategoryFilter}
					/>
					<FilterDropdown
						label="Scope"
						placeholder="All Scopes"
						value={scopeFilter}
						options={SCOPE_TYPES}
						onChange={setScopeFilter}
					/>
					<FilterDropdown
						label="Visibility"
						placeholder="All Visibility"
						value={visibilityFilter}
						options={VISIBILITIES}
						onChange={setVisibilityFilter}
					/>
				</div>
			</div>

			{/* Table */}
			<DataTable
				data={processedArticles}
				columns={columns}
				keyField="id"
				loading={isLoading}
				onRowClick={handleRowClick}
				defaultSort={{ key: 'updatedAt', direction: 'desc' }}
				containerClassName="border border-gray-200 rounded-lg"
				emptyTitle="No articles found"
				emptyDescription="Create your first knowledge base article to get started"
				emptyIcon={BookOpenIcon}
				compact
				pagination
				pageSizeOptions={[10, 25, 50]}
			/>
		</MainContent>
	);
};

export default KnowledgeBase;
